/**
 * RoToast - Toast 提示通用工具方法
 *
 * 功能特性：
 * - 支持 4 种类型：success、error、warning、neutral
 * - 支持 2 种场景：global（带图标）、contextual（纯文本）
 * - 自动定位到屏幕顶部中央
 * - 自动淡入淡出动画
 * - 自动移除 DOM 元素
 *
 * 使用方法：
 * RoToast.success('操作成功！');
 * RoToast.error('操作失败！');
 * RoToast.warning('请注意！');
 * RoToast.neutral('提示信息');
 * RoToast.show('Copied!', { type: 'contextual' });
 */

(function (global) {
  "use strict";

  // SVG 图标定义
  const ICONS = {
    success: `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="8" cy="8" r="8" fill="#4CAF50"/>
      <path d="M4.5 8L7 10.5L11.5 6" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`,
    error: `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="8" cy="8" r="8" fill="#D93840"/>
      <path d="M5 5L11 11M11 5L5 11" stroke="white" stroke-width="1.5" stroke-linecap="round"/>
    </svg>`,
    warning: `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="8" cy="8" r="8" fill="#FF9800"/>
      <path d="M8 5V9M8 11V11.5" stroke="white" stroke-width="1.5" stroke-linecap="round"/>
    </svg>`,
    neutral: `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="8" cy="8" r="8" fill="#9E9E9E"/>
      <path d="M8 5V9M8 11V11.5" stroke="white" stroke-width="1.5" stroke-linecap="round"/>
    </svg>`,
  };

  // Toast 容器（全局单例）
  let toastContainer = null;

  // 获取或创建 Toast 容器
  function getToastContainer() {
    if (!toastContainer) {
      toastContainer = document.createElement("div");
      toastContainer.id = "ro-toast-container";
      toastContainer.style.cssText = `
        position: fixed;
        top: 20px;
        left: 50%;
        transform: translateX(-50%);
        z-index: 10000;
        pointer-events: none;
        display: flex;
        flex-direction: column;
        gap: 8px;
        align-items: center;
      `;
      document.body.appendChild(toastContainer);
    }
    return toastContainer;
  }

  // 创建 Toast 元素
  function createToast(message, options = {}) {
    const {
      type = "neutral", // success | error | warning | neutral
      scope = "global", // global | contextual
      duration = 3000,
    } = options;

    const toast = document.createElement("div");

    // 基础样式
    const baseStyle = `
      min-width: 160px;
      max-width: 400px;
      padding: 12px 16px;
      border-radius: 4px;
      box-shadow: 0 4px 24px rgba(0, 0, 0, 0.12);
      font-size: 14px;
      line-height: 20px;
      pointer-events: auto;
      opacity: 0;
      transform: translateY(-10px);
      transition: opacity 0.3s ease, transform 0.3s ease;
    `;

    // Contextual 样式（黑色背景）
    if (scope === "contextual") {
      toast.style.cssText =
        baseStyle +
        `
        background: rgba(0, 0, 0, 0.9);
        color: white;
        text-align: center;
      `;
      toast.textContent = message;
    } else {
      // Global 样式（白色背景 + 图标）
      toast.style.cssText =
        baseStyle +
        `
        background: white;
        color: #161616;
        display: flex;
        align-items: center;
        gap: 12px;
      `;

      const icon = document.createElement("span");
      icon.style.cssText = `
        flex-shrink: 0;
        width: 16px;
        height: 16px;
        display: inline-flex;
      `;
      icon.innerHTML = ICONS[type] || ICONS.neutral;

      const text = document.createElement("span");
      text.style.cssText = `
        flex: 1;
        max-width: 340px;
        word-break: break-word;
      `;
      text.textContent = message;

      toast.appendChild(icon);
      toast.appendChild(text);
    }

    return toast;
  }

  // 显示 Toast
  function show(message, options = {}) {
    if (!message) return;

    const container = getToastContainer();
    const toast = createToast(message, options);

    container.appendChild(toast);

    // 触发动画（进入）
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        toast.style.opacity = "1";
        toast.style.transform = "translateY(0)";
      });
    });

    // 自动移除
    const duration = options.duration !== undefined ? options.duration : 3000;
    setTimeout(() => {
      // 淡出动画
      toast.style.opacity = "0";
      toast.style.transform = "translateY(-10px)";

      // 移除 DOM
      setTimeout(() => {
        if (toast.parentNode) {
          container.removeChild(toast);
        }
        // 如果容器为空，移除容器
        if (container.children.length === 0) {
          if (container.parentNode) {
            document.body.removeChild(container);
          }
          toastContainer = null;
        }
      }, 300);
    }, duration);
  }

  // 快捷方法
  const RoToast = {
    show,
    success: (message, options = {}) =>
      show(message, { ...options, type: "success" }),
    error: (message, options = {}) =>
      show(message, { ...options, type: "error" }),
    warning: (message, options = {}) =>
      show(message, { ...options, type: "warning" }),
    neutral: (message, options = {}) =>
      show(message, { ...options, type: "neutral" }),
  };

  // 导出到全局
  if (typeof module !== "undefined" && module.exports) {
    module.exports = RoToast;
  } else {
    global.RoToast = RoToast;
  }
})(typeof window !== "undefined" ? window : this);
